import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import type { TargetingConfig } from '../../../types/metaAdDraft';
import { AudienceSection } from '../sections/AudienceSection';
import { createSavedAudience } from '../api';
import { buildSavedAudienceSource, defaultSavedAudienceName } from '../savedAudience';
import { emptyTargeting } from '../emptyDraft';

// Meta Setup hub — standalone audience builder (no ad context). Mounts the SAME
// AudienceSection the ad builder uses (manual build + extract-from-campaign), plus
// a name field, and saves the result as a per-account SavedAudience. The server
// strips placements on save, so a hub-built audience is placement-free exactly like
// a builder-saved one. `initialMode` opens straight into manual or extracted.
export function AudienceBuilderModal({
  accountId,
  accountName,
  fbAdAccountId,
  initialMode,
  onClose,
  onSaved,
}: {
  accountId: string;
  accountName: string;
  fbAdAccountId: string | null;
  initialMode: 'manual' | 'extracted';
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [targeting, setTargeting] = useState<TargetingConfig>(() => emptyTargeting());
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);

  // Until the AM types a name, default from the targeting (the source campaign /
  // ad-set name once extracted, else an account + date fallback). Recomputes as the
  // targeting changes, so extracting populates a sensible default name.
  const effectiveName = nameTouched
    ? name
    : defaultSavedAudienceName(targeting, {
        accountName,
        today: new Date().toISOString().slice(0, 10),
      });

  const save = useMutation({
    mutationFn: () =>
      createSavedAudience(accountId, {
        name: effectiveName.trim(),
        targeting,
        // Extracted targeting carries its source; manual → null. Placements are
        // stripped server-side regardless.
        source: buildSavedAudienceSource(targeting, fbAdAccountId),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['savedAudiences', accountId] });
      toast.success('Audience saved to account');
      onSaved();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not save audience.'),
  });

  const canSave = effectiveName.trim() !== '' && !save.isPending;

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/30 p-4 sm:p-8"
      onClick={() => !save.isPending && onClose()}
    >
      <div
        className="my-4 w-full max-w-3xl rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {initialMode === 'extracted' ? 'Extract audience from a campaign' : 'Create audience'}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Build a reusable audience for{' '}
              <span className="font-medium">{accountName || 'this account'}</span>. Placements aren't
              saved — kyck's placement setting always controls those.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={save.isPending}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-5">
          <div className="max-w-md">
            <label htmlFor="audience-name" className="mb-1.5 block text-sm font-medium text-slate-700">
              Audience name
            </label>
            <input
              id="audience-name"
              type="text"
              value={effectiveName}
              autoFocus
              onChange={(e) => {
                setNameTouched(true);
                setName(e.target.value);
              }}
              placeholder="e.g. Mt Druitt — local women 25–45"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
            />
          </div>

          <AudienceSection
            targeting={targeting}
            accountId={accountId}
            initialMode={initialMode}
            onChange={setTargeting}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending ? 'Saving…' : 'Save audience'}
          </Button>
        </div>
      </div>
    </div>
  );
}
